import { isRejectedWithValue } from "@reduxjs/toolkit";
import {
  showErrorToast,
  removeDataFromLocalStorage,
  removeWebUserFromMeeting,
  triggerUserReauthenticate,
} from "@store/customActions";
import { logOutUser } from "@store/profileSlice";
import { clearMeetingState } from "@store/meetingSlice";
import { clearActivityState } from "@store/activitySlice";
import { clearAgendaState } from "@store/agendasSlice";
import { clearEngagementState } from "@store/engagementSlice";
import { $Routes } from "@routes";
import { isZoomApp } from "@utils/helpers";
import { getFromLocalStorage } from "@utils/localStorage";
import { clearDemoGuestToken } from "@utils/guestDemo";
import { JWT_TOKEN_NAME, LOGOUT_STATUS_CODES } from "@constants";
import { DEFAULT_TOAST_ERROR_MESSAGE } from "@constants/errorMessages";

// Track if we're already processing a logout to prevent duplicates
let isProcessingLogout = false;

const authenticationMiddleware = (api) => (next) => (action) => {
  // check if error is rejected with 401 and logout user
  // TODO: edit to only happen when token cannot be verified or expired
  if (
    isRejectedWithValue(action) &&
    LOGOUT_STATUS_CODES.includes(action.payload.status) &&
    !isZoomApp
  ) {
    // Feature limit errors (e.g. Remix limit, Smart Agenda limit) should NOT
    // trigger logout. They are 403s that the calling component handles by
    // showing an upgrade modal, so we let the action propagate untouched.
    const isFeatureLimitError =
      action.payload.status === 403 &&
      (action.payload?.data?.errorType === "FEATURE_LIMIT" ||
        action.payload?.data?.statusCode === "10001");

    // Phase 2 (guest demo): an anonymous visitor has no JWT, so auth-gated
    // background polls (news, profile, etc.) legitimately 403. There's nothing
    // to log out — skip it so the running demo's state isn't wiped.
    const hasAuthToken = !!getFromLocalStorage(JWT_TOKEN_NAME);

    if (hasAuthToken && !isFeatureLimitError) {
      if (isProcessingLogout) {
        return next(action);
      }

      isProcessingLogout = true;
      api.dispatch(logOutUser(action.payload?.data?.statusCode));

      // Reset the flag after a short delay to allow for new legitimate logout attempts
      setTimeout(() => {
        isProcessingLogout = false;
      }, 1000);
    }
  }

  if (logOutUser.type === action.type) {
    const state = api.getState();
    const meetingState = state.meeting;

    if (meetingState?.data?.inAMeeting && !isZoomApp) {
      const userState = state.profile;
      const userId = userState.data?._id;
      const meetingObject = meetingState?.data?.meetingObject;
      const platformMeetingInstanceId =
        meetingObject?.meetingInstances?.[0].platformMeetingInstanceId;
      api.dispatch(
        removeWebUserFromMeeting({ platformMeetingInstanceId, userId }),
      );
    }

    api.dispatch(removeDataFromLocalStorage(JWT_TOKEN_NAME));
    clearDemoGuestToken();
    api.dispatch(clearMeetingState());
    api.dispatch(clearActivityState());
    api.dispatch(clearAgendaState());
    api.dispatch(clearEngagementState());

    if (!isZoomApp && action.payload === "10003") {
      window.location.replace($Routes.loginPage);
    }
  }

  if (triggerUserReauthenticate.type === action.type) {
    api.dispatch(logOutUser());
  }

  // check if calls to any of the auth endpoints fails and display error message
  if (action.type.endsWith("authApi/executeMutation/rejected")) {
    api.dispatch(
      showErrorToast(
        action.payload?.data?.message || DEFAULT_TOAST_ERROR_MESSAGE,
      ),
    );
  }

  return next(action);
};

export default authenticationMiddleware;
